import { useRef } from "react";
import { motion, useMotionValue, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Activity } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import { Button } from "@/components/ui/button";

const HERO_BG =
  "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&q=85";

const EASE = [0.22, 1, 0.36, 1];

function NocCard() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useTransform(my, [-0.5, 0.5], [7, -7]);
  const rY = useTransform(mx, [-0.5, 0.5], [-9, 9]);

  const bars = [38, 52, 44, 66, 58, 72, 50, 80, 62, 88, 70, 92, 78, 96];

  return (
    <div className="lg:justify-self-end" style={{ perspective: 1200 }}>
      <motion.div
        ref={ref}
        data-testid="hero-noc-card"
        onMouseMove={(e) => {
          const r = ref.current.getBoundingClientRect();
          mx.set((e.clientX - r.left) / r.width - 0.5);
          my.set((e.clientY - r.top) / r.height - 0.5);
        }}
        onMouseLeave={() => {
          mx.set(0);
          my.set(0);
        }}
        style={{ rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.05, ease: EASE }}
        className="glass w-full max-w-md rounded-2xl p-6 shadow-[0_24px_80px_rgba(2,6,23,0.6)]"
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
            {t.noc.title}
          </span>
          <Activity className="h-4 w-4 text-orange-400" />
        </div>

        <div className="mt-4 flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
          </span>
          <span className="text-sm font-semibold text-amber-300">{t.noc.status}</span>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            { label: t.noc.uptime, value: "99.98%" },
            { label: t.noc.latency, value: "8 ms" },
            { label: t.noc.throughput, value: "2.4 Gb/s" },
          ].map((m) => (
            <div
              key={m.label}
              className="rounded-xl border border-slate-700/40 bg-slate-950/60 px-3 py-3"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {m.label}
              </div>
              <div className="mt-1 font-display text-base font-bold text-orange-300 [text-shadow:0_0_18px_rgba(251,146,60,0.45)] sm:text-lg">
                {m.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex h-14 items-end gap-1.5">
          {bars.map((b, i) => (
            <motion.div
              key={i}
              className="flex-1 rounded-sm bg-gradient-to-t from-orange-500/70 to-amber-400/70"
              initial={{ height: "8%" }}
              animate={{ height: `${b}%` }}
              transition={{ duration: 0.9, delay: 1.3 + i * 0.06, ease: EASE }}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-slate-500">
          <span>NOC · UAE → EU</span>
          <span>{t.noc.note}</span>
        </div>
      </motion.div>
    </div>
  );
}

export default function Hero() {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 170]);
  const fadeBg = useTransform(scrollY, [0, 700], [1, 0.25]);

  const lines = [
    { text: t.hero.l1, accent: false },
    { text: t.hero.l2, accent: false },
    { text: t.hero.l3, accent: true },
  ];

  return (
    <section id="top" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: yBg, opacity: fadeBg }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src={HERO_BG}
          alt=""
          className="h-[115%] w-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/60 to-slate-950" />
      </motion.div>

      <div className="grid-bg absolute inset-0 -z-10 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,black,transparent)]" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -z-10 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-orange-500/15 blur-[140px]" aria-hidden="true" />

      <div className="mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8 lg:pt-28">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-orange-400/25 bg-orange-400/5 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-orange-300 sm:text-[11px]"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              {t.hero.eyebrow}
            </motion.p>

            <h1 className="mt-7 font-display text-[2.35rem] font-extrabold leading-[1.06] tracking-tighter text-slate-50 sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
              {lines.map((l, i) => (
                <span key={i} className="-mb-1 block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${l.accent ? "text-gradient" : ""}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.95, delay: 0.35 + i * 0.15, ease: EASE }}
                    data-testid={`hero-line-${i + 1}`}
                  >
                    {l.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
              data-testid="hero-sub"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Button
                data-testid="hero-cta-audit"
                onClick={() => scrollToId("#contact")}
                className="group h-12 rounded-full bg-orange-400 px-7 text-sm font-semibold text-slate-950 shadow-[0_0_32px_rgba(251,146,60,0.4)] transition-all duration-300 hover:scale-[1.04] hover:bg-orange-300 hover:shadow-[0_0_48px_rgba(251,146,60,0.6)]"
              >
                {t.hero.cta1}
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
              <Button
                data-testid="hero-cta-services"
                onClick={() => scrollToId("#services")}
                variant="ghost"
                className="group h-12 rounded-full border border-slate-600/60 px-6 text-sm font-medium text-slate-200 transition-colors duration-300 hover:border-orange-400/50 hover:text-orange-300"
              >
                {t.hero.cta2}
                <ArrowDown className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </Button>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <NocCard />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 flex flex-col gap-4 border-t border-slate-800/60 pt-6 sm:flex-row sm:items-center sm:justify-between"
          data-testid="hero-footer-strip"
        >
          <span className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            <span className="h-px w-8 bg-gradient-to-r from-orange-400 to-amber-400" />
            {t.hero.scroll}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {t.hero.stackLabel} <span className="text-slate-400">{t.hero.stackBrands}</span>
          </span>
        </motion.div>
      </div>
    </section>
  );
}
